import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Layers, LayersOutlined } from '@mui/icons-material';
import { useSingleAndDoubleClick } from '../../utils';
import {
  useProjectDnDContext,
  useProjectDnDStyles,
} from './project-dnd.context';
import { useProjectNode } from './project-dnd.hook';
import { ClickAwayListener, IconButton, Tooltip } from '@mui/material';

export interface DnDNodeProps {
  id: number;
  nodeType:
    | 'project'
    | 'stage'
    | 'grouping'
    | 'task'
    | 'event'
    | 'coordination'
    | 'item';
  parentId: number;
  parentNodeType:
    | 'index'
    | 'project'
    | 'stage'
    | 'grouping'
    | 'task'
    | 'event'
    | 'coordination'
    | 'item';
  props?: any;
  data?: any;
  startExpanded?: boolean;
  children: Array<DnDNodeProps>;
  sequence: number;
  expandParent?: Function;
}

export const DnDNode = forwardRef((props: DnDNodeProps, ref) => {
  const {
    id,
    nodeType,
    startExpanded = false,
    data = {},
    children = [],
    expandParent,
  } = props;
  const {
    itemsRef,
    tree,
    render,
    onClickAway = () => {},
    onNodeClick = (payload: any) => {},
    onNodeDoubleClick = (payload: any) => {},
    onContextMenu = (e: any, payload: any) => {},
  } = useProjectDnDContext();
  const {
    draggerStyle,
    dropperPrevStyle,
    dropperNextStyle,
    nodeStyle,
    containerStyle,
  } = useProjectDnDStyles();
  const {
    drag,
    dropToNode,
    dropToNext,
    dropToPrev,
    isDragging,
    isOverNode,
    isOverNext,
    isOverPrev,
    canDropToNode,
    canDropToNext,
    canDropToPrev,
  } = useProjectNode(props);
  const [isClickExpanded, setIsClickExpanded] =
    useState<boolean>(startExpanded);
  const onSingleClick = () => {
    onNodeClick(props);
  };
  const onDoubleClick = () => {
    onNodeDoubleClick(props);
  };
  const onClick = useSingleAndDoubleClick(onSingleClick, onDoubleClick);
  const scrollRef = useRef<HTMLElement>(null);
  useImperativeHandle(ref, () => ({
    expandParent: () => {
      expandParent && expandParent();
    },
    expand: () => {
      setIsClickExpanded(true);
    },
    onClick: onClick,
    node: props,
    scrollRef: scrollRef,
  }));

  return (
    <div
      ref={drag}
      style={
        (id !== tree?.id || nodeType !== tree.nodeType) && nodeType !== 'stage'
          ? containerStyle
          : {}
      }
    >
      <div
        ref={dropToPrev}
        style={{
          paddingTop: isOverPrev && canDropToPrev ? 5 : 0,
          paddingBottom: isOverPrev && canDropToPrev ? 10 : 0,
        }}
      >
        <div style={dropperPrevStyle(isOverPrev, canDropToPrev)} />
      </div>
      {!children || children.length < 1 ? (
        <ClickAwayListener onClickAway={() => onClickAway()}>
          <div
            // ref={dropToNode}
            ref={(el: any) => {
              dropToNode(el);
              ref = el;
            }}
            style={nodeStyle(isDragging, isOverNode, canDropToNode)}
            onClick={onClick}
            onContextMenu={e => onContextMenu(e, props)}
          >
            <span ref={scrollRef}></span>
            <IconButton disabled sx={{ visibility: 'hidden' }}>
              <Layers />
            </IconButton>
            {render
              ? render({ ...props, expanded: true }, children)
              : typeof data === 'string'
              ? data
              : id}
          </div>
        </ClickAwayListener>
      ) : (
        <ClickAwayListener onClickAway={() => onClickAway()}>
          <Accordion
            disableGutters
            elevation={0}
            expanded={isClickExpanded}
            onContextMenu={e => onContextMenu(e, props)}
            onClick={onClick}
            sx={{ width: '100%', p: 0 }}
          >
            <AccordionSummary sx={{ width: '100%', p: 0 }}>
              <div
                // ref={dropToNode}
                ref={(el: any) => {
                  dropToNode(el);
                  ref = el;
                }}
                style={{
                  ...nodeStyle(isDragging, isOverNode, canDropToNode),
                  ...{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                  },
                }}
              >
                <span ref={scrollRef}></span>
                <Tooltip
                  title={
                    isClickExpanded ? 'Click to collapse' : 'Click to expand'
                  }
                >
                  <IconButton
                    id={'layers-expand'}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      setIsClickExpanded(!isClickExpanded);
                    }}
                    sx={{ mt: 0, pb: 0 }}
                  >
                    {isClickExpanded ? (
                      <LayersOutlined
                        sx={{
                          WebkitFilter: 'invert(50%)',
                          filter: 'invert(50%)',
                        }}
                      />
                    ) : (
                      <Layers />
                    )}
                  </IconButton>
                </Tooltip>
                {render
                  ? render(
                      {
                        ...props,
                        expanded: isClickExpanded,
                        expand: () => setIsClickExpanded(true),
                      },
                      children
                    )
                  : typeof data === 'string'
                  ? data
                  : id}
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ width: '100%', p: 0 }}>
              {children &&
                children?.length > 0 &&
                children?.map(child => (
                  <div key={`${child.nodeType}-${child.data?.id}`}>
                    <DnDNode
                      ref={(el: any) => {
                        (itemsRef?.current as any)[`item-${child?.id}`] = el;
                      }}
                      {...child}
                      expandParent={() => {
                        expandParent && expandParent();
                        setIsClickExpanded(true);
                      }}
                    />
                  </div>
                ))}
            </AccordionDetails>
          </Accordion>
        </ClickAwayListener>
      )}
      <div
        ref={dropToNext}
        style={{
          paddingTop: isOverNext && canDropToNext ? 10 : 0,
          paddingBottom: isOverNext && canDropToNext ? 5 : 0,
        }}
      >
        <div style={dropperNextStyle(isOverNext, canDropToNext)} />
      </div>
    </div>
  );
});
